
// @flow
import * as React from 'react';
import { Box, Button, Text } from 'grommet';
import { FormattedMessage } from 'react-intl';
import connectAPI from 'speed-js-react/src/api/connectAPI';
import { HTTP_400_BAD_REQUEST } from 'speed-js-core';
import type { ApiClient } from 'speed-js-core';
import {
    set, get, isEmpty, unset, isEqual,
} from 'lodash';
import { Formik } from 'formik';
import scrollToFirstError
    from 'speed-js-react-web/src/components/grommet2/forms/hooks/scrollToFirstError';
import memoizeOne from 'memoize-one';
import cloneDeep from 'lodash/cloneDeep';
import Spinner from 'speed-js-react-web/src/components/Spinner';
import GrommetForm from '../../../components/grommet2/forms/GrommetForm';

type Props = {
    // eslint-disable-next-line react/no-unused-prop-types
    resourceName: string,
    // eslint-disable-next-line
    resourceArguments?: Array<any>;
    // eslint-disable-next-line react/no-unused-prop-types
    nestedSchemaQuery: Object,
    // eslint-disable-next-line react/no-unused-prop-types
    optionsResourceArguments?: Array<any>;
    objectId: string | number,
    getInitial: () => Promise<Object>,
    initial: Object,
    // eslint-disable-next-line react/no-unused-prop-types
    onSave?: Function,
    // eslint-disable-next-line react/no-unused-prop-types
    nestedInitials: boolean,
    // eslint-disable-next-line react/no-unused-prop-types
    excludeFields?: Array<string>,
    // eslint-disable-next-line react/no-unused-prop-types
    schemaResourceMethod: string,
    onSubmit: Function,
    actions?: Object,
    // eslint-disable-next-line react/no-unused-prop-types
    partialUpdate: boolean,
    forwardRef: React.ElementRef<Formik>,
    // eslint-disable-next-line react/no-unused-prop-types
    onError: Function,
    // eslint-disable-next-line react/no-unused-prop-types
    useResourceMethodForSchema?: boolean,
    useInitial: boolean,
    onChange: ?Function,
    // eslint-disable-next-line react/no-unused-prop-types
    unsetPaths?: Array<string>,
    // eslint-disable-next-line react/no-unused-prop-types
    extraSchema?: Object,
    onSuccess?: Function,
    enableReinitialize?: boolean,
    onValuesChanged: Function,
    // eslint-disable-next-line react/no-unused-prop-types
    onSchemaRetrieval: Function,
    onFormMounted?: () => void,
    getGrommetFormBoxRef?: Function,
    children: Object,
    hasActions?: boolean,
    validateOnBlur?: boolean,
    validateOnChange?: boolean,
}

const locales = {
    submit: (
        <FormattedMessage
            id="apiform.submit"
            defaultMessage="Submit"
        />
    ),
};

type CombinedProps = Props & {
    getSchema: () => Promise<Object>,
    submit: (data: Object, formik: Object) => Promise<Object>,
    schemaResult: Object,
    schemaErrors: Array<string>,
};
@connectAPI((apiClient: ApiClient, props: CombinedProps) => ({
    getSchema: {
        send: () => {
            const resourceArguments = !isEmpty(props.optionsResourceArguments)
                ? props.optionsResourceArguments
                : props.resourceArguments;
            if (props.resourceMethod && props.useResourceMethodForSchema) {
                return get(apiClient.resources, props.resourceName)
                    .schemaMethod(props.objectId, props.resourceMethod, ...resourceArguments);
            }

            return get(apiClient.resources, props.resourceName).schema(...resourceArguments, props.nestedSchemaQuery);
        },
        success: (schemaResult: Object) => {
            if (props.onSchemaRetrieval) {
                // Return a deep clone of the schema
                props.onSchemaRetrieval(JSON.parse(JSON.stringify(schemaResult)));
            }
            return { schemaResult };
        },
        error: (response: Object) => {
            const { onError } = props;
            if (onError) onError(response);
            return { schemaErrors: response.data };
        },
    },
    getInitial: {
        send: () => {
            if (props.nestedInitials) {
                return get(apiClient.resources, props.resourceName).get(...props.resourceArguments, props.objectId);
            }
            if (props.resourceMethod) {
                return get(apiClient.resources, props.resourceName)[props.resourceMethod](
                    ...props.resourceArguments, props.objectId, { nonested: 1 },
                );
            }
            return get(apiClient.resources, props.resourceName).get(
                ...props.resourceArguments, props.objectId, { nonested: 1 },
            );
        },
        success: (initial: Object) => (props.initial ? { initial: { ...initial, ...props.initial } } : { initial }),
    },
    submit: {
        send: (data: Object) => {
            if (props.resourceMethod) {
                return get(apiClient.resources, props.resourceName)[props.resourceMethod](
                    ...props.resourceArguments, data,
                );
            }
            if (props.submitResourceMethod) {
                return get(apiClient.resources, props.resourceName)[props.submitResourceMethod](
                    ...props.resourceArguments, data,
                );
            }

            if (props.objectId) {
                if (props.partialUpdate) {
                    return get(apiClient.resources, props.resourceName).partialUpdate(
                        ...props.resourceArguments, props.objectId, data,
                    );
                }

                return get(apiClient.resources, props.resourceName).update(
                    ...props.resourceArguments, props.objectId, data,
                );
            }
            return get(apiClient.resources, props.resourceName).create(...props.resourceArguments, data);
        },
        success: (response: { resourceName: Object }, data: Object, formik: Object) => {
            formik.setSubmitting(false);
            formik.resetForm();
        },
        error: (response: Object, data: Object, formik: Object) => {
            const { onError } = props;
            formik.setSubmitting(false);
            formik.setErrors(response.data);
            if (onError) onError(response);
            // if (response.status === HTTP_400_BAD_REQUEST) {
            //     formik.setErrors(response.data);
            // }
        },
    },
}), true)
class ApiForm extends React.Component<CombinedProps> {
    static defaultProps: Object;

    formRef: React.ElementRef<ApiForm>;

    unmounting: boolean;

    applyExtraSchema = memoizeOne((schemaResult, extraSchema) => {
        if (!extraSchema) {
            return schemaResult;
        }

        const schema = cloneDeep(schemaResult);
        if (extraSchema) {
            // Each entry is an object where the keys are the path to the property in the schema
            // and the values are the values for that property
            extraSchema.forEach((entry) => {
                // For each object element in the array gets the key and value pairs
                // and sets the value for that property in the schema object.
                Object.entries(entry).forEach((e) => {
                    const [path, value] = e;
                    const delimiterIndex = path.indexOf('.');
                    const fieldName = path.substring(0, delimiterIndex);
                    const pathName = path.substring(delimiterIndex + 1);
                    const field = schema.fields.find((schemaField) => schemaField[0] === fieldName);
                    if (field) {
                        set(field[1], pathName, value);
                    }
                });
            });
        }
        return schema;
    });

    excludeSchemaFields = memoizeOne((schemaResult, excludeFields, unsetPaths) => {
        // Deep clone schemaResult in order to remove excluded fields from schema without modifying schemaResult
        if (!excludeFields && !unsetPaths) {
            return schemaResult;
        }
        const schema = cloneDeep(schemaResult);
        if (excludeFields) {
            schema.fields = schema.fields.filter((value) => excludeFields.indexOf(value[0]) === -1);
        }
        if (unsetPaths) {
            unsetPaths.forEach((path) => {
                const delimiterIndex = path.indexOf('.');
                const fieldName = path.substring(0, delimiterIndex);
                const pathName = path.substring(delimiterIndex + 1);
                const field = schema.fields.find((schemaField) => schemaField[0] === fieldName);
                if (field) {
                    unset(field[1], pathName);
                }
            });
        }
        return schema;
    })

    constructor(props: Props) {
        super(props);

        this.unmounting = false;
        this.formRef = React.createRef();
    }

    componentDidMount() {
        const {
            getSchema, objectId, getInitial, useInitial,
        } = this.props;
        if (objectId && useInitial) {
            getInitial();
        }
        getSchema();
    }

    componentDidUpdate(prevProps: Readonly<P>, prevState: Readonly<S>, snapshot: SS): void {
        const { resourceName: prevResourceName, optionsResourceArguments: prevOptionsResourceArguments } = prevProps;
        const {
            resourceName, getSchema, objectId, getInitial, useInitial,
            optionsResourceArguments, schemaResult, onFormMounted,
        } = this.props;

        if (prevResourceName !== resourceName) {
            if (objectId && useInitial) {
                getInitial();
            }
            getSchema();
        }

        if (!isEqual(prevOptionsResourceArguments, optionsResourceArguments)) {
            getSchema();
        }

        if (schemaResult && prevProps.schemaResult !== schemaResult && onFormMounted) {
            onFormMounted();
        }
    }

    componentWillUnmount(): void {
        this.unmounting = true;
    }

    _getFormValues = async (formRef) => {
        try {
            await formRef.current.getFormikBag().validateForm();
            const { values, errors } = formRef.current.getFormikBag();
            return { values, errors };
        } catch (e) {
            return {};
        }
    }

    handleChange = (event) => {
        const { onChange, forwardRef, onValuesChanged } = this.props;
        if (onValuesChanged) {
            const selectedRef = (forwardRef && forwardRef.current) ? forwardRef : this.formRef;
            this._getFormValues(selectedRef).then((formikData) => {
                if (!this.unmounting) {
                    return onValuesChanged(formikData.values, formikData.errors);
                }
                return formikData.values;
            });
        }
        onChange(event);
    };

    handleSubmit = (values: Object, formik: Object) => {
        const {
            submit, onSubmit, onSuccess, onSave,
        } = this.props;
        let newValues;
        if (onSubmit) {
            newValues = onSubmit(values, formik);
        }
        submit(newValues || values, formik).then((response) => {
            if (response.status === HTTP_400_BAD_REQUEST) {
                scrollToFirstError(response.data);
            } else if (onSuccess) {
                onSuccess(response);
            }
            if (onSave) {
                onSave(response);
            }
        });
    };

    renderActions = ({ isSubmitting }: { isSubmitting: boolean }) => (
        <Box dirextion="row" margin={{ vertical: 'small' }} width="small">
            <Button fill label={locales.submit} type="submit" alignSelf="end" disabled={isSubmitting} />
        </Box>
    );

    render() {
        const {
            schemaResult, initial, objectId, actions, forwardRef, enableReinitialize, excludeFields, extraSchema,
            unsetPaths, schemaErrors, getGrommetFormBoxRef, children, hasActions, validateOnBlur, validateOnChange,
        } = this.props;

        if (!schemaResult || !isEmpty(schemaErrors) || (objectId && !initial)) {
            return (
                <Box>
                    {schemaErrors ? (
                        <Box
                            background="status-error"
                            margin={{ vertical: 'small' }}
                            pad="small"
                            round="small"
                        >
                            <Text color="white">
                                {Array.isArray(schemaErrors) ? schemaErrors.join('; ') : null}
                                {schemaErrors.detail ? schemaErrors.detail : null}
                            </Text>
                        </Box>
                    ) : (
                        <Box pad={{ top: 'medium' }}>
                            <Spinner
                                type="clipLoader"
                                loading
                                blocking={false}
                                color="black"
                                coverStyle={{
                                    position: 'relative',
                                    width: 'auto',
                                    height: 'auto',
                                }}
                            />
                        </Box>
                    )}
                    {!hasActions && (actions({ isSubmitting: false }, true))}
                </Box>
            );
        }

        // Apply extra schema
        let schema = this.applyExtraSchema(schemaResult, extraSchema);

        // Exclude fields and unset paths
        schema = this.excludeSchemaFields(schema, excludeFields, unsetPaths);

        return (
            // $FlowFixMe
            <GrommetForm
                ref={forwardRef || this.formRef}
                schema={schema}
                initial={initial}
                onChange={this.handleChange}
                onSubmit={this.handleSubmit}
                actions={!hasActions && (actions || this.renderActions)}
                height="100%"
                enableReinitialize={enableReinitialize}
                getGrommetFormBoxRef={getGrommetFormBoxRef}
                validateOnBlur={validateOnBlur}
                validateOnChange={validateOnChange}
            >
                {children}
            </GrommetForm>
        );
    }
}

ApiForm.defaultProps = {
    nestedInitials: false,
    resourceArguments: [],
    nestedSchemaQuery: {},
    optionsResourceArguments: [],
    excludeFields: undefined,
    partialUpdate: false,
    useResourceMethodForSchema: true,
    useInitial: true,
    onChange() {},
    onSuccess() {},
    onSave() {},
    unsetPaths: undefined,
    extraSchema: undefined,
    enableReinitialize: false,
    onValuesChanged: undefined,
    onSchemaRetrieval: undefined,
    onFormMounted: undefined,
    getGrommetFormBoxRef: () => {},
    actions: null,
    hasActions: false,
    validateOnBlur: true,
    validateOnChange: true,
};

export default ApiForm;
